import { useState } from "react";
import { useOpsMe, type OpsRole } from "../api/me.js";
import {
  roleLabels,
  roleOptions,
  staffErrorMessage,
  useInviteStaff,
  useResendStaffInvite,
  useSetStaffRole,
  useStaff,
  type StaffMember,
} from "../api/staff.js";

/**
 * Team — who can sign in to this console and what they can do.
 *
 * Admin-only, top to bottom: the roster is the access-control surface, so a
 * support or analyst staffer sees the explanation below rather than a list of
 * who to target. The server enforces the same rule on all three routes; this
 * gate only decides what is worth rendering.
 *
 * This screen replaces provisioning staff by hand with the AWS CLI. Inviting
 * someone creates their Cognito account and emails them a temp password;
 * resending mails them a fresh one when the first expires; changing a role
 * rewrites their group membership.
 */
const FIELD_CLASS = "w-full rounded-lg border border-charcoal/30 px-3 py-2 text-sm text-charcoal";

/** Cognito's lifecycle states, in the words an operator would use. */
function statusLabel(member: StaffMember): { text: string; className: string } {
  if (!member.enabled) return { text: "Disabled", className: "bg-rose-100 text-rose-700" };
  switch (member.status) {
    case "CONFIRMED":
      return { text: "Active", className: "bg-emerald-100 text-emerald-800" };
    case "FORCE_CHANGE_PASSWORD":
      return { text: "Invited", className: "bg-amber-100 text-amber-800" };
    case "RESET_REQUIRED":
      return { text: "Password reset", className: "bg-amber-100 text-amber-800" };
    default:
      return { text: member.status.replace(/_/g, " "), className: "bg-field-gray text-charcoal" };
  }
}

export function Team() {
  const { data: me, isPending: mePending } = useOpsMe();
  const isAdmin = (me?.roles ?? []).includes("admin");
  const { data, isPending, isError, refetch } = useStaff(isAdmin);
  const [inviting, setInviting] = useState(false);

  if (mePending) return <p className="text-sm text-charcoal">Loading…</p>;

  if (!isAdmin) {
    return (
      <div>
        <h1 className="text-xl font-bold text-charcoal">Team</h1>
        <p className="mt-4 text-sm text-charcoal">
          Only admins can view or change who has access to this console. Ask an admin if you need a
          role changed.
        </p>
      </div>
    );
  }

  const items = data?.items ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-charcoal">Team</h1>
          <p className="mt-1 text-sm text-charcoal">
            Everyone who can sign in to the Operations Console.
          </p>
        </div>
        {!inviting && (
          <button
            type="button"
            onClick={() => setInviting(true)}
            className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white"
          >
            Invite staff member
          </button>
        )}
      </div>

      {inviting && <InviteCard onDone={() => setInviting(false)} />}

      {isPending ? (
        <p className="mt-6 text-sm text-charcoal">Loading…</p>
      ) : isError ? (
        <div className="mt-6">
          <p className="text-sm text-charcoal">Couldn&rsquo;t load the team.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 rounded-lg border border-charcoal/30 px-3 py-1.5 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      ) : (
        <table className="mt-4 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-charcoal/15 text-left text-xs uppercase tracking-wide text-charcoal">
              <th className="py-2 pr-4 font-medium">Member</th>
              <th className="py-2 pr-4 font-medium">Role</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Added</th>
            </tr>
          </thead>
          <tbody>
            {items.map((member) => (
              <MemberRow
                key={member.sub}
                member={member}
                // /ops/me reports the signed-in staffer by email, and email is
                // the pool's unique sign-in alias, so this identifies the same
                // person the server recognizes by sub.
                isSelf={member.email.toLowerCase() === (me?.email ?? "").toLowerCase()}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * One member, with their role editable in place. A role change doesn't reach
 * the member until their next sign-in — Cognito only re-issues
 * `cognito:groups` with a new token — so say so instead of letting an admin
 * assume it took effect and wonder why it didn't.
 */
function MemberRow({ member, isSelf }: { member: StaffMember; isSelf: boolean }) {
  const setRole = useSetStaffRole();
  const [changed, setChanged] = useState<OpsRole | null>(null);
  const current = member.roles[0];
  const status = statusLabel(member);
  const name = [member.givenName, member.familyName].filter(Boolean).join(" ");

  return (
    <tr className="border-b border-charcoal/10 hover:bg-white">
      <td className="py-2 pr-4">
        <div className="font-medium text-charcoal">{member.email}</div>
        {name && <div className="text-xs text-charcoal">{name}</div>}
      </td>
      <td className="py-2 pr-4">
        {isSelf ? (
          // The server refuses this too. Showing why beats a control that
          // always fails: it is the rule that stops an admin from removing
          // the console's last admin — their own account.
          <div>
            <span className="rounded-full bg-field-gray px-2 py-0.5 text-xs font-semibold text-charcoal">
              {current ? roleLabels[current] : "No role"}
            </span>
            <div className="text-xs text-charcoal">You — ask another admin to change this.</div>
          </div>
        ) : (
          <div>
            <select
              aria-label={`Role for ${member.email}`}
              value={current ?? ""}
              disabled={setRole.isPending}
              onChange={(e) => {
                const role = e.target.value as OpsRole;
                setRole.mutate({ sub: member.sub, role }, { onSuccess: () => setChanged(role) });
              }}
              className={FIELD_CLASS}
            >
              {!current && <option value="">No role</option>}
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {changed && (
              <div className="mt-1 text-xs text-charcoal">
                Now {roleLabels[changed]}. Takes effect when they next sign in.
              </div>
            )}
            {setRole.error && (
              <div className="mt-1 text-xs text-rose-700">{staffErrorMessage(setRole.error)}</div>
            )}
          </div>
        )}
      </td>
      <td className="py-2 pr-4">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${status.className}`}>
          {status.text}
        </span>
        {/*
          Sits under the status badge because the status is the reason it is
          there: "Invited" is precisely the state a resend is for, and the
          control disappears the moment the member becomes Active.

          Gated on `enabled` as well as the status, not on the badge text — a
          disabled invitee still carries FORCE_CHANGE_PASSWORD while rendering
          as "Disabled", and mailing a fresh password to an account that can't
          sign in would be a dead end. The server refuses that case too.
        */}
        {member.enabled && member.status === "FORCE_CHANGE_PASSWORD" && (
          <ResendInvite member={member} />
        )}
      </td>
      <td className="py-2 pr-4 text-charcoal">
        {new Date(member.createdAt).toLocaleDateString()}
      </td>
    </tr>
  );
}

/**
 * Mail a fresh invitation to someone whose first one went stale.
 *
 * Temporary passwords last 7 days (`tempPasswordValidity`, ops identity
 * stack) and the pool is admin-create-only, so an invitee past that window
 * has no self-serve way back in — without this they stay stuck until an admin
 * reaches for the CLI.
 *
 * The note after a send is the part that matters. A resend issues a *new*
 * temporary password, so the one already sitting in the invitee's inbox stops
 * working the instant it succeeds. An admin resending "just in case" while
 * someone is midway through signing up would otherwise break the very link
 * they were trying to help with, and never learn why.
 */
function ResendInvite({ member }: { member: StaffMember }) {
  const resend = useResendStaffInvite();
  const [sent, setSent] = useState(false);

  // `sent` survives a later failure on purpose: a password that went out
  // earlier is still live in the invitee's inbox, so the label keeps offering
  // "Resend again" rather than pretending nothing was ever sent. The note
  // itself yields to an error — react-query clears `error` when the next
  // attempt starts, so the two are never both true.
  const label = resend.isPending ? "Sending…" : sent ? "Resend again" : "Resend invite";

  return (
    <div className="mt-1">
      <button
        type="button"
        // The visible label is inside the accessible name, not replaced by it:
        // once this reads "Resend again", a voice-control user saying what
        // they can see still has to match something (WCAG 2.5.3). The email
        // is what tells the two rows apart.
        aria-label={`${label} to ${member.email}`}
        disabled={resend.isPending}
        onClick={() => resend.mutate({ sub: member.sub }, { onSuccess: () => setSent(true) })}
        className="text-xs font-semibold text-brand-700 disabled:opacity-50"
      >
        {label}
      </button>
      {sent && !resend.error && (
        <div role="status" className="mt-1 text-xs text-charcoal">
          New invitation sent. It expires in 7 days, and the password from the earlier one no
          longer works.
        </div>
      )}
      {resend.error && (
        <div role="alert" className="mt-1 text-xs text-rose-700">
          {staffErrorMessage(resend.error)}
        </div>
      )}
    </div>
  );
}

/**
 * Invite a new staffer. Cognito sends the invitation and the temp password
 * itself, from the same verified domain the rest of the product mails from —
 * nothing here ever handles a password, and there is nothing for the admin to
 * relay by hand.
 */
function InviteCard({ onDone }: { onDone: () => void }) {
  const invite = useInviteStaff();
  const [email, setEmail] = useState("");
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [role, setRole] = useState<OpsRole>("admin");
  const [invited, setInvited] = useState<string | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    invite.mutate(
      {
        email: trimmed,
        role,
        ...(givenName.trim() ? { givenName: givenName.trim() } : {}),
        ...(familyName.trim() ? { familyName: familyName.trim() } : {}),
      },
      {
        onSuccess: (r) => {
          setInvited(r.member.email);
          setEmail("");
          setGivenName("");
          setFamilyName("");
        },
      },
    );
  }

  return (
    <section className="mt-4 rounded-2xl border border-charcoal/15 bg-white p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-charcoal">
        Invite staff member
      </h2>
      <div className="mt-2">
        {invited ? (
          <div className="space-y-2 text-sm">
            <p className="text-charcoal">
              Invited <strong>{invited}</strong>. They&rsquo;ll get an email with a temporary
              password and set their own on first sign-in.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setInvited(null);
                  invite.reset();
                }}
                className="text-sm font-semibold text-brand-700"
              >
                Invite someone else
              </button>
              <button
                type="button"
                onClick={onDone}
                className="text-sm font-semibold text-charcoal"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="staff-email" className="block text-xs font-medium text-charcoal">
                  Work email (required)
                </label>
                <input
                  id="staff-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@qchomeandbusinessprojectpros.com"
                  className={`mt-1 ${FIELD_CLASS}`}
                />
              </div>
              <div>
                <label htmlFor="staff-role" className="block text-xs font-medium text-charcoal">
                  Role
                </label>
                <select
                  id="staff-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as OpsRole)}
                  className={`mt-1 ${FIELD_CLASS}`}
                >
                  {roleOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-charcoal">
                  {roleOptions.find((r) => r.value === role)?.blurb}
                </p>
              </div>
              <div>
                <label htmlFor="staff-given" className="block text-xs font-medium text-charcoal">
                  First name (optional)
                </label>
                <input
                  id="staff-given"
                  value={givenName}
                  maxLength={100}
                  onChange={(e) => setGivenName(e.target.value)}
                  className={`mt-1 ${FIELD_CLASS}`}
                />
              </div>
              <div>
                <label htmlFor="staff-family" className="block text-xs font-medium text-charcoal">
                  Last name (optional)
                </label>
                <input
                  id="staff-family"
                  value={familyName}
                  maxLength={100}
                  onChange={(e) => setFamilyName(e.target.value)}
                  className={`mt-1 ${FIELD_CLASS}`}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={invite.isPending || email.trim().length === 0}
                className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Send invite
              </button>
              <button
                type="button"
                onClick={onDone}
                className="rounded-lg border border-charcoal/30 px-4 py-2 text-sm font-semibold text-charcoal"
              >
                Cancel
              </button>
            </div>
            {invite.error && (
              <p className="text-sm text-rose-700">{staffErrorMessage(invite.error)}</p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
